import type { AssistantMessage, ContentPart, ToolCall } from '../../../../types'
import { getTopLevelToolCalls } from './tool-call-utils'

export type MessagePartRef =
  | { type: 'text'; text: string; partIndex: number }
  | { type: 'tool_call'; toolCallId: string }

/**
 * The ONE "prefer `contentParts`, else fall back to `content` + `toolCalls`"
 * resolution. Every walk that turns a message into ordered render parts —
 * `buildRenderParts` below and the group derivation in `message/index.tsx` —
 * starts here, so a `contentParts` format change lands in one place.
 *
 * Emits references, not resolved objects: `partIndex` is the part's index in
 * the ORIGINAL `contentParts` array (the stable render-key contract in
 * parts/registry), and tool calls come back as ids for the caller to resolve
 * against whichever tool-call scope it renders. Whitespace-only text is
 * dropped here — every consumer did so at some layer already.
 */
export function resolveMessagePartRefs(
  message: Pick<AssistantMessage, 'content' | 'contentParts' | 'toolCalls'>,
): Array<MessagePartRef> {
  const refs: Array<MessagePartRef> = []

  if (message.contentParts && message.contentParts.length > 0) {
    message.contentParts.forEach((cp, partIndex) => {
      if (cp?.type === 'text' && typeof cp.text === 'string' && cp.text.trim()) {
        refs.push({ type: 'text', text: cp.text, partIndex })
      } else if (cp?.type === 'tool_call' && typeof cp.toolCallId === 'string') {
        refs.push({ type: 'tool_call', toolCallId: cp.toolCallId })
      }
    })
    return refs
  }

  const content = typeof message.content === 'string' ? message.content : ''
  if (content.trim()) {
    refs.push({ type: 'text', text: content, partIndex: 0 })
  }
  for (const tc of message.toolCalls ?? []) {
    if (tc?.id) {
      refs.push({ type: 'tool_call', toolCallId: tc.id })
    }
  }
  return refs
}

/**
 * The reload-side inverse of `resolveMessagePartRefs`: the `contentParts` a
 * refetched assistant message should carry, rebuilt from the flat
 * content-plus-tool-calls shape the backend persists. Tools lead and the
 * response text trails, so the renderer folds the tools into the collapsible
 * and reveals the text as the turn's answer — the same split a streamed
 * message gets. Returns undefined when there is nothing to lay out.
 */
export const reconstructContentParts = (
  toolCalls: Array<ToolCall>,
  content: string,
): Array<ContentPart> | undefined => {
  const topLevelToolCalls = getTopLevelToolCalls(toolCalls)
  if (topLevelToolCalls.length === 0) return undefined
  const parts: Array<ContentPart> = topLevelToolCalls.map((tc) => ({ type: 'tool_call', toolCallId: tc.id }))
  if (content.trim()) parts.push({ type: 'text', text: content })
  return parts
}

/**
 * A single message's resolved render parts: its part refs with tool calls
 * resolved to the message's own top-level calls (set-aware, so an orphaned
 * child still renders), plus any top-level calls `contentParts` never
 * referenced appended at the end. Text parts keep their original `partIndex`
 * — it is the part's key identity, and refs dropped here (a subagent child
 * tool call) must not shift it.
 */
export const buildRenderParts = (
  message: AssistantMessage,
): Array<{ type: 'text'; text: string; partIndex: number } | { type: 'tool_call'; toolCall: ToolCall }> => {
  const topLevelToolCalls = getTopLevelToolCalls(message.toolCalls ?? [])
  const topLevelById = new Map(topLevelToolCalls.map((tc) => [tc.id, tc]))

  const parts: Array<{ type: 'text'; text: string; partIndex: number } | { type: 'tool_call'; toolCall: ToolCall }> = []
  const referencedToolCallIds = new Set<string>()

  for (const ref of resolveMessagePartRefs(message)) {
    if (ref.type === 'text') {
      parts.push({ type: 'text', text: ref.text, partIndex: ref.partIndex })
      continue
    }
    const tc = topLevelById.get(ref.toolCallId)
    if (tc) {
      parts.push({ type: 'tool_call', toolCall: tc })
      referencedToolCallIds.add(tc.id)
    }
  }

  for (const tc of topLevelToolCalls) {
    if (!referencedToolCallIds.has(tc.id)) {
      parts.push({ type: 'tool_call', toolCall: tc })
    }
  }

  return parts
}
